// CPK-3930: touch commit so Bitbucket's changeset check for this build sees
// frontend/** as modified (its Frontend deploy step is otherwise skipped when
// the last commit in a promotion range doesn't itself touch this path) — no
// functional change below.
import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxMaskDirective } from 'ngx-mask';
import { Observable, Subscription, debounceTime, map } from 'rxjs';
import { SpeechSynthesisService } from 'src/app/core/services/speech-synthesis.service';
import { ViaCepService } from 'src/app/core/services/viacep.service';
import { countries } from 'src/app/shared/components/simple/question-country/question-country.component';
import { MaterialModule } from 'src/app/shared/modules/material/material-module.module';
import {
  AddressValidationPromptAddress,
  AddressValidationPromptData,
  AddressValidationPromptPayload,
  AddressValidationPromptResult,
} from './address-validation-prompt.types';

// Mirrors BRAZIL_COUNTRY_NAMES in question-world-address.component.ts and
// lambdas/src/endpoint/service/address/fieldValidation.ts — same set, same
// case-insensitive/trimmed match, so "is this a Brazilian address" agrees
// everywhere it's checked.
const BRAZIL_COUNTRY_NAMES = new Set(['brazil', 'brasil', 'br']);

export const BRAZIL_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'AC', name: 'Acre' },
  { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' },
  { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' },
  { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' },
  { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' },
  { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' },
  { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' },
  { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' },
  { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' },
  { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' },
  { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' },
  { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' },
  { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' },
  { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' },
];

// CPK-4323: replaces the country-agnostic cepDigitsValidator (CPK-4239) — that one
// blocked any all-digit postal code that wasn't exactly 8 digits, including valid
// 5-digit US ZIPs. Only Brazil actually has an 8-digit CEP rule.
function brazilianCepValidator(viaCep: ViaCepService): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value ?? '';
    if (!value) return null; // required is a separate validator
    return viaCep.isValidBrazilianCep(value) ? null : { brazilCep: true };
  };
}

@Component({
  selector: 'app-address-validation-prompt',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, TranslateModule, NgxMaskDirective],
  templateUrl: './address-validation-prompt.component.html',
  styleUrls: ['./address-validation-prompt.component.scss'],
})
export class AddressValidationPromptComponent implements OnInit, OnDestroy {
  private readonly speechIdPrefix = `address-validation-${Math.random().toString(36).slice(2)}`;
  private readonly confirmNextActions = new Set([
    'CONFIRM',
  ]);

  viewMode: 'confirm' | 'edit';
  enteredAddressSpeaking$: Observable<boolean>;
  readonly brazilStates = BRAZIL_STATES;
  // CPK-4293: bound to the country's full name (e.g. "Brazil"), not its 2-letter
  // code — this address pipeline (Google Places results, ViaCepService, the
  // existing free-text data already on file) stores country as a name everywhere;
  // question-country.component.ts stores the code instead, but that's an unrelated
  // question type. Reusing its country list here, just not its value convention.
  readonly countries = countries;

  // CPK-4239: CEP is the first field — filling it looks up the rest via ViaCEP,
  // mirroring the offline-mode flow already in QuestionWorldAddressComponent.
  cepLoading = false;
  // Mirrors PlaceAdditionalInfo.resolvedVia — reset to 'manual' as soon as the CEP is
  // edited, and only flipped to 'viacep' once a lookup for that value actually succeeds.
  private resolvedVia: 'manual' | 'viacep' = 'manual';
  private readonly subscriptions: Subscription[] = [];

  readonly form = new FormGroup({
    postalCode: new FormControl('', Validators.required),
    streetName: new FormControl('', Validators.required),
    streetNumber: new FormControl('', Validators.required),
    unitNumber: new FormControl(''),
    buildingName: new FormControl(''),
    referencePoint: new FormControl(''),
    neighborhood: new FormControl(''),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    country: new FormControl('', Validators.required),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddressValidationPromptData,
    private dialogRef: MatDialogRef<
      AddressValidationPromptComponent,
      AddressValidationPromptResult
    >,
    public speech: SpeechSynthesisService,
    private translate: TranslateService,
    private viaCep: ViaCepService,
  ) {
    this.dialogRef.disableClose = true;
    this.viewMode = data?.mode ?? 'confirm';
    this.enteredAddressSpeaking$ = this.speech.speaking$.pipe(
      map((id) => id === this.speechId()),
    );
  }

  get isBrazilCountry(): boolean {
    return BRAZIL_COUNTRY_NAMES.has((this.form.get('country')?.value ?? '').trim().toLowerCase());
  }

  ngOnInit(): void {
    this.form.patchValue(this.toEditableAddress(this.data?.enteredAddress ?? {}));

    this.applyPostalCodeValidators();
    this.subscriptions.push(
      this.form.get('country')!.valueChanges.subscribe(() => this.applyPostalCodeValidators()),
      this.form.controls.postalCode.valueChanges.subscribe(() => {
        this.resolvedVia = 'manual';
      }),
      this.form.controls.postalCode.valueChanges
        .pipe(debounceTime(800))
        .subscribe((cep) => void this.lookupCep(cep ?? '')),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private async lookupCep(cep: string): Promise<void> {
    if (!this.viaCep.isValidBrazilianCep(cep)) return;

    this.cepLoading = true;
    try {
      const result = await this.viaCep.searchByCep(cep);
      if (!result) return;

      const mapped = this.viaCep.mapToAddressFields(result);
      this.form.patchValue({
        // CPK-4337: mapped.postalCode is ViaCEP's response reformatted to XXXXX-XXX
        // (see formatCep in viacep.service.ts) — without this the field keeps
        // whatever raw digits the client typed and the dash never reaches the DB.
        postalCode: mapped.postalCode || this.form.controls.postalCode.value,
        streetName: mapped.streetName || this.form.controls.streetName.value,
        neighborhood: mapped.neighborhood || this.form.controls.neighborhood.value,
        city: mapped.city || this.form.controls.city.value,
        state: mapped.state || this.form.controls.state.value,
        country: mapped.country || this.form.controls.country.value,
      });
      this.resolvedVia = 'viacep';
    } finally {
      this.cepLoading = false;
    }
  }

  // CPK-4293: strict 8-digit CEP validation only makes sense for Brazil — other
  // countries' postal codes (UK, US, etc.) go through this same shared form.
  // Re-applied whenever `country` changes so switching in/out of Brazil updates
  // the rule instead of leaving a stale validator from the previous country.
  private applyPostalCodeValidators(): void {
    const control = this.form.get('postalCode');
    if (!control) return;

    const validators = [Validators.required];
    if (this.isBrazilCountry) {
      validators.push(brazilianCepValidator(this.viaCep));
    }
    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  get enteredAddressText(): string {
    return this.formatAddress(this.data?.enteredAddress);
  }

  get possibleNextAction(): string | null {
    const action = this.data?.validationPayload?.possible_next_action;
    return action ? String(action).toUpperCase() : null;
  }

  get canConfirmAddress(): boolean {
    // CPK-4239: once retries are exhausted the backend refuses to re-check with
    // Google, so editing again can't help — always allow self-certifying instead
    // of leaving the user stuck.
    if (this.retryLimitReached) return true;
    // CPK-4239 fix: an AGENT/ADMINISTRATOR caller has no retry ceiling (Infinity), so
    // retryLimitReached above never fires for them — without this they could loop on
    // "Modify address" forever whenever Google keeps returning FIX instead of CONFIRM.
    if (this.data?.validationPayload?.caller_unlimited_retries) return true;
    return !this.possibleNextAction || this.confirmNextActions.has(this.possibleNextAction);
  }

  get retryLimitReached(): boolean {
    return this.data?.validationPayload?.retry_limit_reached === true;
  }

  get actionInstructionKey(): string {
    switch (this.possibleNextAction) {
      case 'CONFIRM':
        return 'AddressValidationConfirmInstruction';
      case 'CONFIRM_ADD_SUBPREMISES':
        return 'AddressValidationConfirmAddSubpremisesInstruction';
      case 'FIX':
        return 'AddressValidationFixInstruction';
      default:
        return 'AddressValidationDefaultInstruction';
    }
  }

  canSpeakAddress(): boolean {
    return this.speech.isSupported && this.enteredAddressText.trim().length > 0;
  }

  toggleSpeakAddress(): void {
    const id = this.speechId();
    if (this.speech.isSpeakingId(id)) {
      this.speech.stop();
      return;
    }

    this.speech.speak(id, this.enteredAddressText, this.speechLang());
  }

  switchToEdit(): void {
    this.speech.stop();
    this.viewMode = 'edit';
  }

  backToConfirm(): void {
    this.viewMode = 'confirm';
  }

  confirmOverride(): void {
    if (!this.canConfirmAddress) {
      return;
    }

    const now = new Date().toISOString();
    const currentPayload = this.data?.validationPayload ?? { status: 'failed' };
    const addressValidation: AddressValidationPromptPayload = {
      ...currentPayload,
      status: 'user_confirmed',
      user_confirmed_override: true,
      checked_at: currentPayload.checked_at ?? now,
    };

    this.speech.stop();
    this.dialogRef.close({
      action: 'confirmed_override',
      address: this.data?.enteredAddress,
      addressValidation,
    });
  }

  submitModifiedAddress(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.speech.stop();
    this.dialogRef.close({
      action: 'modified_address',
      address: this.getEditedAddress(),
      addressValidation: this.data?.validationPayload ?? undefined,
    });
  }

  cancel(): void {
    this.speech.stop();
    this.dialogRef.close({ action: 'cancelled' });
  }

  private speechId(): string {
    return `${this.speechIdPrefix}-entered`;
  }

  private speechLang(): string {
    const lang = (this.translate.currentLang || this.translate.defaultLang || '').toLowerCase();
    return lang.startsWith('pt') ? 'pt-BR' : 'en-US';
  }

  private getEditedAddress(): AddressValidationPromptAddress {
    const raw = this.form.getRawValue();
    return {
      streetNumber: raw.streetNumber || null,
      streetName: raw.streetName || null,
      unitNumber: raw.unitNumber || null,
      buildingName: raw.buildingName || null,
      referencePoint: raw.referencePoint || null,
      neighborhood: raw.neighborhood || null,
      city: raw.city || null,
      state: raw.state || null,
      country: raw.country || null,
      postalCode: raw.postalCode || null,
      resolvedVia: this.resolvedVia,
    };
  }

  private toEditableAddress(
    address: AddressValidationPromptAddress,
  ): Record<string, string> {
    return {
      streetNumber: address.streetNumber ?? '',
      streetName: address.streetName ?? '',
      unitNumber: address.unitNumber ?? '',
      buildingName: address.buildingName ?? '',
      referencePoint: address.referencePoint ?? '',
      neighborhood: address.neighborhood ?? '',
      city: address.city ?? '',
      state: this.normalizeBrazilState(address.state ?? ''),
      country: this.normalizeCountryName(address.country ?? ''),
      postalCode: address.postalCode ?? '',
    };
  }

  // CPK-4293 (code review finding): Google Places autocomplete — the default,
  // most common capture path (see google-places.service.ts, `state = component
  // .long_name`) — stores the state's full name ("São Paulo"), not its 2-letter
  // code ("SP"). Without this, the new state <mat-select> (bound to codes only)
  // would render as unselected for the majority of already-captured Brazilian
  // addresses, even though the control still held a valid value underneath —
  // confusing enough to make a user "fill in" a field that already had the
  // right answer, and risk swapping it for the wrong one from the dropdown.
  private normalizeBrazilState(rawState: string): string {
    const trimmed = rawState.trim();
    if (!trimmed) return '';
    const match = BRAZIL_STATES.find(
      (s) => s.code.toLowerCase() === trimmed.toLowerCase() || s.name.toLowerCase() === trimmed.toLowerCase(),
    );
    return match ? match.code : trimmed; // unrecognized: leave as-is, select shows unselected
  }

  // Same normalization, for the same reason, now that country is also a select —
  // "Brasil"/"BR" (both seen in real data) need to resolve to the list's one
  // canonical spelling ("Brazil") to pre-select correctly.
  private normalizeCountryName(rawCountry: string): string {
    const trimmed = rawCountry.trim();
    if (!trimmed) return '';
    if (BRAZIL_COUNTRY_NAMES.has(trimmed.toLowerCase())) return 'Brazil';
    const match = countries.find(
      (c) => c.name.toLowerCase() === trimmed.toLowerCase() || c.code.toLowerCase() === trimmed.toLowerCase(),
    );
    return match ? match.name : trimmed;
  }

  private formatAddress(
    address?: AddressValidationPromptAddress | string | null,
  ): string {
    if (!address) return '';
    if (typeof address === 'string') return address;

    if (address.formattedAddress) return address.formattedAddress;

    const street = [address.streetNumber, address.streetName].filter(Boolean).join(' ');
    return [
      address.addressLine1,
      street,
      address.unitNumber,
      address.buildingName,
      address.neighborhood,
      address.city,
      address.state || address.county,
      address.postalCode || address.postcode,
      address.country,
    ]
      .filter((part) => !!part && String(part).trim().length > 0)
      .join(', ');
  }
}
